
import {useReducer} from 'react';
import {ApolloClient, useApolloClient} from '@apollo/client';
import {Location} from 'components/TimePicker/types';
import moment from 'moment-timezone';
import {sortkey} from 'utils/sort';

import {filterLocation} from './filter';
import {getEvents, Result, Vars} from './query';
import {EventVars} from '.';

const maxParallelRunners = 4;
const maxEventsPerRequest = 20;

export type State = {
	client: ApolloClient<object>
	dispatch: React.Dispatch<Action>
	vars: EventVars
	token: string
	inflight: number

	entries: Record<string, {
		index: number,
		location: Location,
	}>
	remaining: Vars[]
	locations: Location[]
};

type Action =
  | { type: 'SET-DATA', data: Result, token: string }
  | { type: 'SET-DISPATCH', dispatch: React.Dispatch<Action> }
  | { type: 'SET-REMAINING', remaining: Vars[], token: string }
  | { type: 'SET-VARS', vars: EventVars }
  | { type: 'QUERY-START' }
  | { type: 'QUERY-NEXT' }
  | { type: 'COUNT-INC' }
  | { type: 'COUNT-DEC' }
;

const increment = (dispatch: React.Dispatch<Action>) => {
	dispatch({type: 'COUNT-INC'});
	return () => dispatch({type: 'COUNT-DEC'});
};

const reducer = (state: State, action: Action): State => {
	switch (action.type) {

	// SET-DATA aggregates edges from query results onto state
	case 'SET-DATA': {
		if (action.token !== state.token) {
			return {...state};
		}

		const entries = {...state.entries};
		const locations = [...state.locations];

		// Process all incoming edges/nodes from latest data
		for (const {node} of action.data.events.edges) {

			// Check if an entry for this location already exists
			// If not, create and add to locations & entries
			if (!entries[node.location.id]) {
				const entry = {
					index: locations.length,
					location: {
						id: node.location.id,
						datesBase: [],
						datesLeft: [],
						details: node.location.details,
						photos: node.location.photos,
						regions: node.location.regions,
					},
				};

				locations.push(entry.location);
				entries[node.location.id] = entry;
			}

			// Load our location from entries table
			const {location} = entries[node.location.id];

			// If date is already processed, skip
			const match = location.datesBase.find((s) => s.date === node.date.date);
			if (match) {
				continue;
			}

			// Create & add our date to the list of dateSlots
			location.datesBase = [
				...location.datesBase,
				{
					index: -1,
					...node.date,
					timesBase: node.appts.map((appt, index) => ({
						index,
						...appt,
						...appt.details.start,
					})),
					timesLeft: [],
					isPrivate: node.private,
				},
			]
				// Sort each slot by increasing date
				.sort(sortkey((d) => d.date))

				// Re-index all dates; middle adds possible
				.map((d, index) => ({...d, index}));

			// Create a subset of dates with open appointments
			location.datesLeft = filterLocation(location);
		}

		// Sort locations depending on first open date
		// Put empty locations last in the list
		locations.sort(sortkey((l) => {
			if (l.datesLeft.length === 0) {
				return '9999-99-99';
			}
			return l.datesLeft[0].date;
		}));

		return {
			...state,
			entries,
			locations,
		};
	}

	// SET-DISPATCH is called close to initialization
	// and sets the dispatch function on the state object
	case 'SET-DISPATCH':
		return {...state, dispatch: action.dispatch};

	// SET-VARS clears our current state and sets a
	// new region/slug & token to handle any in-flight requests
	case 'SET-VARS':
		return {
			...state,

			entries: {},
			remaining: [],
			locations: [],
			vars: {
				...action.vars,
			},

			token: moment().unix().toString(),
		};

	// SET-REMAINING is called after the response from QUERY-START
	// and configures the remaining parallelized requests
	case 'SET-REMAINING':
		if (action.token !== state.token) {
			return {...state};
		}
		return {...state, remaining: action.remaining};

	// QUERY-START is called when we're ready to make the first
	// request. It then saves the data & calls SET-REMAINING.
	case 'QUERY-START': {
		const perPage = maxEventsPerRequest;
		const maxPages = state.vars.maxPages;

		const filter = {
			region: state.vars.region,
			location: state.vars.slug,
			startDate: moment().format('YYYY-MM-DD'),
			endDate: state.vars.endDate,
		};
		const decrement = increment(state.dispatch);

		getEvents(state.client, {
			variables: {
				filter,
				page: {
					first: perPage,
				}
			},
		}).then(({data}) => {
			const total = data.events.pageInfo.total;
			const remaining: Vars[] = [];

			for (let next = perPage; next < total; next += perPage) {
				if (maxPages && remaining.length >= maxPages - 1) {
					break;
				}

				remaining.push({
					filter,
					page: {
						first: perPage,
						after: `${next - 1}`,
					},
				});
			}

			// Initialize our remaining queries
			state.dispatch({
				type: 'SET-REMAINING',
				remaining,
				token: state.token,
			});

			// Add our most recent result to the state
			state.dispatch({
				type: 'SET-DATA',
				data,
				token: state.token,
			});

			// Trigger N parallel runners to pull tasks from remaining
			const numRunners = Math.min(maxParallelRunners, remaining.length);
			for (let i = 0; i < numRunners; i++) {
				state.dispatch({type: 'QUERY-NEXT'});
			}
		}).catch(() => {
			return;
		}).finally(() => {
			decrement();
		});

		return {...state};
	}

	// QUERY-NEXT checks if there is a remaining request
	// and if so, processes it by generating a parallel query
	case 'QUERY-NEXT':
		if (state.remaining.length > 0) {
			const decrement = increment(state.dispatch);

			getEvents(state.client, {
				variables: {
					...state.remaining[0],
				},
			}).then(({data}) => {
				// Add our most recent result to the state
				state.dispatch({
					type: 'SET-DATA',
					data,
					token: state.token,
				});

				// Trigger an additional following request
				state.dispatch({type: 'QUERY-NEXT'});
			}).catch(() => {
				return;
			}).finally(() => {
				decrement();
			});

			return {
				...state,
				remaining: state.remaining.slice(1),
			};
		}

		// If no remainings, exit without changes
		return {...state};

	case 'COUNT-INC':
		return {
			...state,
			inflight: state.inflight + 1,
		};

	case 'COUNT-DEC':
		return {
			...state,
			inflight: state.inflight - 1,
		};
	}
};

const initState = (client: ApolloClient<object>): State => {
	return {
		client,
		dispatch: () => null,
		entries: {},
		inflight: 0,
		locations: [],
		remaining: [],
		token: '',
		vars: {},
	};
};

export const useState = () => {
	const client = useApolloClient();
	return useReducer(reducer, client, initState);
};
